import { useMutation } from "@tanstack/solid-query";
import { isYeetWorkerStalled } from "contracts/api/crawl/run-read.responses";
import { Show } from "solid-js";
import type { ScanInput } from "../../store/query";
import { createAbortRun, createRunState } from "../runs/runQueries";
import type { WebsiteContext } from "../website/store";
import { activeRunSummary } from "./activeRunSummary";

export function ScanRunControls(props: { context: WebsiteContext }) {
  const data = () => props.context.data();
  const runState = createRunState(props.context.websiteId);
  const activeRun = runState.run;
  // Fleet-wide, not run-scoped: the predicate reads the shared yeet queue, so
  // the wording must not imply this website's scan specifically is broken.
  const workersBackedUp = () => isYeetWorkerStalled(runState.yeetWorker());
  const scan = useMutation<void, Error, ScanInput>(() => ({
    mutationKey: ["scan"],
  }));
  const scanError = () => scan.error?.message ?? null;

  const abort = createAbortRun(props.context.websiteId);
  const runSummary = () =>
    activeRunSummary(props.context.websiteId, activeRun(), workersBackedUp());

  return (
    <div class="signal-run-controls">
      <Show
        when={runSummary()}
        keyed
        fallback={
          <button
            class="signal-scan-btn"
            type="button"
            onClick={() => scan.mutate({ websiteId: props.context.websiteId })}
            disabled={scan.isPending || !data()}
            title={scanError() ?? "Run a new crawl of this website"}
          >
            {scan.isPending ? "Starting scan" : "Run scan"}
          </button>
        }
      >
        {(run) => (
          <>
            <a
              class="signal-scan-btn signal-active-run"
              href={run.href}
              title={run.accessibleLabel}
              aria-label={run.accessibleLabel}
            >
              {run.label}
            </a>
            <Show when={run.backedUp}>
              <span class="badge badge-sev sev-medium" role="status">
                Analysis workers are backed up
              </span>
            </Show>
            <Show when={run.canCancel}>
              <button
                class="signal-scan-btn signal-scan-cancel"
                type="button"
                onClick={() => abort.mutate(run.runId)}
                disabled={abort.isPending}
                title="Cancel the running scan and fail its outstanding pages"
              >
                {abort.isPending ? "Cancelling…" : "Cancel"}
              </button>
            </Show>
          </>
        )}
      </Show>
      <Show when={scanError()}>
        <span class="signal-scan-error" role="alert">
          {scanError()}
        </span>
      </Show>
    </div>
  );
}
